import React, { useState, useEffect, useRef, useContext } from 'react';
import { CREATE_PARTY, CREATE_PARTY_PERMISSION_MESSAGE, CREATE_PERMISSION, DELETE_PERMISSION, MANAGE_PARTY, READ_PERMISSION, UPDATE_PARTY_PERMISSION_MESSAGE, UPDATE_PERMISSION } from './Utils/Constants';
import CustomSearchBar from 'Components/GMS/Products/components/CustomSearchBar';
import PartyTable from './Party/PartyTable';
import { TPaginationParams } from 'Pages/GMS/Performance/analytics';
import { CreatePartyDialog } from './Party/CreatePartyDialog';
import { TParty, TCreatePartyPayload } from './Types/party.types';
import Paginator from 'Components/Common/PaginatorWithApi';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AppContext } from 'Context/AppContext';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import AppSettings from 'Settings/AppSettings';

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

const ManageParty = () => {
    const history = useHistory();
    const appContext = useContext(AppContext);

    const readPermission = appContext.pm.checkPermission(READ_PERMISSION);
    const createPermission = appContext.pm.checkPermission(CREATE_PERMISSION);
    const updatePermission = appContext.pm.checkPermission(UPDATE_PERMISSION);
    const deletePermission = appContext.pm.checkPermission(DELETE_PERMISSION);

    const [paginationParams, setPaginationParams] = useState<TPaginationParams>({pageNumber: 1, pageSize: 10});
    const [isCreatePartyOpen, setIsCreatePartyOpen] = useState(false);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [parties, setParties] = useState<TParty[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [sortBy, setSortBy] = useState<string>('name');
    const [sortOrder, setSortOrder] = useState<string>('asc');
    const [isCreating, setIsCreating] = useState<boolean>(false);
    
    const [editingPartyDialog, setEditingPartyDialog] = useState<{
        isOpen: boolean;
        party: TParty | null;
    }>({ isOpen: false, party: null });
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    
    const isMounted = useRef(true);
    
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);
    
    useEffect(() => {
        if (!readPermission) {
            history.push("/workspace/module/permission/denied");
            return;
        }
        fetchParties();
    }, [paginationParams, searchTerm, sortBy, sortOrder]);

    // useEffect(() => {
    //     const unlisten = history.listen((location, action) => {
    //         if (action === 'POP') {
    //             //fetchParties();
    //         }
    //     });
        
    //     return () => {
    //         unlisten();
    //     };
    // }, [history]);


    const fetchParties = async () => {
        setShowLoader(true);
        try {
            const partyResponse = await ServiceWrapper.doGet( 
                `${api_base_url}user/party/list?page=${paginationParams.pageNumber}&per_page=${paginationParams.pageSize}&sort_by=${sortBy}&sort_order=${sortOrder}&search_string=${searchTerm}`, 
                null, 
                null, 
                AuthManager.getGmsToken()
            ); 
            const response = partyResponse?.data;
            
            if (isMounted.current && response && response.data) {
                setParties(response.data);
                setTotalRecords(response.pagination.total);
            }
        } catch (error) {
            console.error('Error fetching parties:', error);
            if (isMounted.current) {
                toast.error('Failed to fetch parties. Please try again.');
            }
        } finally {
            if (isMounted.current) {
                setShowLoader(false);
            }
        }
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setPaginationParams(prev => ({ ...prev, pageNumber: 1 }));
    };

    const handleCreateParty = async (formData: any) => {
        if (!createPermission) {
            toast.warn(CREATE_PARTY_PERMISSION_MESSAGE);
            return;
        }
        setIsCreating(true);
        
        try {
            const payload: TCreatePartyPayload = {
                name: formData.partyName,
                address: formData.address,
                party_type: formData.partyType
            };
            const partyResponse = await ServiceWrapper.doPost( 
                `${api_base_url}party/create`, 
                payload, 
                null, 
                AuthManager.getGmsToken()
            );
            const response = partyResponse?.data;
            if (response && response.done) {
                toast.success('Party created successfully!');
                setIsCreatePartyOpen(false);
                fetchParties();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                if (isMounted.current) {
                    toast.error('Failed to create party. Please try again.');
                    setIsCreatePartyOpen(false);
                }
            }
        } catch (error) {
            console.error('Error creating party:', error);
            if (isMounted.current) {
                toast.error('An error occurred while creating the party.');
                setIsCreatePartyOpen(false);
            }
        } finally {
            if (isMounted.current) {
                setIsCreating(false);
            }
        }
    };

    const handleEditParty = (party: TParty, index: number) => {
        if (!updatePermission) {
            toast.warn(UPDATE_PARTY_PERMISSION_MESSAGE);
            return;
        }
        setEditingPartyDialog({ 
            isOpen: true, 
            party: party 
        });
    };

    const handleUpdateParty = async (formData: any) => {
        if (!updatePermission) {
            toast.warn(UPDATE_PARTY_PERMISSION_MESSAGE);
            return;
        }
        
        const party = editingPartyDialog.party;
        if (!party) return;
        
        setIsUpdating(true);
        
        try {
            
            const partyResponse = await ServiceWrapper.doPost( 
                `${api_base_url}party/edit/${party.id}`, 
                formData, 
                null, 
                AuthManager.getGmsToken()
            );
            const response = partyResponse?.data;
            
            if (response && response.done) {
                toast.success('Party updated successfully!');
                setEditingPartyDialog({ isOpen: false, party: null });
                
                await fetchParties();
                
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to update party');
            }
        } catch (error) {
            console.error('Error updating party:', error);
            toast.error('An error occurred while updating the party');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleNavigateToDomain = (party: TParty) => {
        history.push(`/admin/party/domain/${party?.id ?? ''}`);
    };

    const handlePageChange = (pageNumber: number) => {
        setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageNumber: pageNumber,
        }));
    };
    
    const updatePageSize = (pageSize: number) => {
        setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageSize: pageSize,
            pageNumber: 1
        }));
    };

    const handleSortChange = (column: string) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
        setPaginationParams(prev => ({ ...prev, pageNumber: 1 }));
    };

    const onCreatePartyClick = () => {
        if (!createPermission) {
            toast.warn(CREATE_PARTY_PERMISSION_MESSAGE);
            return;
        }
        setIsCreatePartyOpen(true);
    }

    return (
        <>
            <div className={`tw-flex tw-items-center tw-justify-between tw-border-b tw-border-[#d1d1d6] tw-pb-2`}>
                <label className={`tw-text-black tw-text-xl tw-font-semibold`}>
                    {MANAGE_PARTY}
                </label>
                <button onClick={onCreatePartyClick} className={`button-style tw-w-max`}>
                    {CREATE_PARTY}
                </button>
            </div>
            <div className='tw-w-[25%] tw-mt-4'>
                <CustomSearchBar 
                    handleSearchChange={handleSearchChange} 
                    placeholder='Search' 
                />
            </div>
            <PartyTable 
                parties={parties}
                onEditParty={handleEditParty}
                showLoader={showLoader}
                onNavigateToDomain={handleNavigateToDomain}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
                onPartyDeleted={fetchParties}
                deletePermission={deletePermission}
                updatePermission={updatePermission}
            />
            {!showLoader && parties.length > 0 ?
                <div id="pagination-container" className="tw-py-2">
                    <div className={`tw-flex tw-justify-end tw-items-center tw-space-x-3 tw-flex-wrap`}>
                        <div className={`tw-flex tw-items-center tw-space-x-3`}>
                            <span>Show</span>
                            <select
                                id="page-size-select"
                                className={`tw-border focus:tw-outline-none tw-p-1`}
                                style={{ width: "80px" }}
                                aria-label="Records on page select"
                                value={paginationParams?.pageSize ?? 10}
                                onChange={(e) => {
                                    updatePageSize(parseInt(e?.target?.value));
                                }}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                            </select>
                        </div>
                        <Paginator
                            onPageChange={handlePageChange}
                            recordPerPage={paginationParams?.pageSize}
                            presentPage={paginationParams?.pageNumber}
                            totalRecords={totalRecords} 
                        />
                    </div>
                </div> : null}
            
            <CreatePartyDialog 
                isOpen={isCreatePartyOpen}
                onClose={() => setIsCreatePartyOpen(false)}
                onSave={handleCreateParty}
                isLoading={isCreating}
                mode="create"
            />
            
            <CreatePartyDialog
                isOpen={editingPartyDialog.isOpen}
                onClose={() => setEditingPartyDialog({ isOpen: false, party: null })}
                onSave={handleUpdateParty}
                isLoading={isUpdating}
                editData={editingPartyDialog.party}
                mode="edit"
            />
        </>
    );
}

export default ManageParty;